/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-common
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import PropTypes from 'prop-types';
import BaseInput from '@ba-ui-toolkit/baseinput';
import time32 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/time_32';
import momentPropTypes from 'react-moment-proptypes';
import Moment from 'moment';
import moment from 'moment-timezone';
import TimeSlot from './TimeSlot';
import styles from './TimePicker.scss';
import stringResource from './nls/StringResource';

const timepickerClassName = styles.timepickerClassName.replace(/"/g, '');
const { sanitize } = propsUtils;

const A = ['AM', 'PM'];

function getString(value) {
	return stringResource.get(value);
}

function toDoubleDigit(num) {
	if (parseInt(num, 10) < 10) {
		return `0${num}`;
	}

	return num;
}

export default class TimePicker extends Component {
	static format(value) {
		return value.format('hh:mm A');
	}

	static propTypes = {
		/** To set direction */
		dir: PropTypes.oneOf(['rtl', 'ltr']),
		/** Set initial focused state */
		focused: PropTypes.bool,
		/** Allow seconds edit */
		showSec: PropTypes.bool,
		/** Disable time picker */
		disabled: PropTypes.bool,
		/** Function that is called when time is changed */
		onChange: PropTypes.func,
		/** Function that is called when focus is changed */
		onFocusChange: PropTypes.func,
		/** Initial time */
		time: momentPropTypes.momentObj,
		/** Classname to be given to the container of TagInput */
		className: PropTypes.string,
		/** Custom CSS styling */
		style: PropTypes.object,
		getLocaleString: PropTypes.func,
		timeInputExtraOptions: PropTypes.object,
	};

	static defaultProps = {
		onFocusChange: () => {},
		focused: null,
		onChange: () => {},
		className: '',
		style: {},
		timeInputExtraOptions: {},
		getLocaleString: getString,
	};

	state = {
		initialFoucsed: false,
	};

	componentWillMount() {
		const { time } = this.props;
		this._updateInternalTime(this._getValidTime(time));
	}
	componentDidMount() {
		// Must use document to let the listener cover the parent domNode
		document.addEventListener('click', this._onClickAway);
	}
	componentWillReceiveProps(nextProps) {
		const { time } = nextProps;

		if (time && (!this.props.time || this.props.time.format() !== time.format())) {
			this._updateInternalTime(this._getValidTime(time));
		}
	}
	componentWillUnmount() {
		document.removeEventListener('click', this._onClickAway);
	}

	_getValidTime(time) {
		let validTime;
		if (Moment.isMoment(time) && time.isValid()) {
			validTime = moment(time);
		} else {
			console.warn(
				`Your time prop value, ${time}, is not valid, the current time is used instead.`
			); // eslint-disable-line no-console
			validTime = moment();
		}

		return validTime;
	}

	_getFormattedTime = () => {
		let newTime;
		const { showSec } = this.props;
		const { time } = this.state;
		if (showSec) {
			const { h, m, a, s } = time;
			const value = `${h}:${m}:${s} ${A[parseInt(a, 10)]}`;
			const tempMoment = moment(value, 'HH:mm:ss a');
			newTime = moment(this.props.time)
				.hour(tempMoment.hour())
				.minute(tempMoment.minute())
				.second(tempMoment.second());
		} else {
			const { h, m, a } = time;
			const value = `${h}:${m} ${A[parseInt(a, 10)]}`;
			const tempMoment = moment(value, 'HH:mm a');
			newTime = moment(this.props.time)
				.hour(tempMoment.hour())
				.minute(tempMoment.minute());
		}

		return newTime;
	};
	_getNewTimeByUnit = (num, unit) => {
		const newTime = { ...this.state.time };
		newTime[unit] = num;

		return newTime;
	};

	_updateInternalTime(time) {
		let timeString;
		if (this.props.showSec) {
			timeString = time.format('hh:mm:ss A');
		} else {
			timeString = time.format('hh:mm A');
		}

		const timeArr = timeString.replace(' ', ':').split(':');

		const newTime = {};
		if (this.props.showSec) {
			const [hour, min, sec, ampm] = timeArr;
			newTime.a = ampm.toLowerCase() === 'am' ? 0 : 1;
			newTime.h = parseInt(hour, 10);
			newTime.m = parseInt(min, 10);
			newTime.s = parseInt(sec, 10);
			this.setState({
				time: newTime,
				value: `${newTime.h}:${toDoubleDigit(newTime.m)}:${toDoubleDigit(newTime.s)} ${
					newTime.a === 1 ? 'PM' : 'AM'
				}`,
			});
		} else {
			const [hour, min, ampm] = timeArr;
			newTime.a = ampm.toLowerCase() === 'am' ? 0 : 1;
			newTime.h = parseInt(hour, 10);
			newTime.m = parseInt(min, 10);
			this.setState({
				time: newTime,
				value: `${newTime.h}:${toDoubleDigit(newTime.m)} ${newTime.a === 1 ? 'PM' : 'AM'}`,
			});
		}
	}
	_onChange = v => {
		let time = [0, 0, 0];
		if (v !== '') {
			time = v.match(/[0-9]+/g);
		}
		if (time) {
			const [hour, min, sec] = time.map(t => parseInt(t, 10));
			const newStates = {
				value: v,
				time: {
					h: hour,
					m: min,
					s: sec,
				},
			};
			const lastTwoString = v.slice(-2).toUpperCase();
			if (lastTwoString === 'AM') {
				newStates.time.a = 0;
			} else if (lastTwoString === 'PM') {
				newStates.time.a = 1;
			}

			if (hour > 12) {
				newStates.time.h = hour % 12 || hour; // [1-11] || 12
				newStates.time.a = 1;
			} else if (hour < 1) {
				newStates.time.h = 12;
				newStates.time.a = 0;
			} else {
				newStates.time.h = hour;
			}

			if (min && min > 59) newStates.time.m = 59;
			if (sec && sec > 59) newStates.time.s = 59;
			this.setState(newStates);
		}
	};

	_ref = node => {
		this._node = node;
	};
	_inputRef = node => {
		this.inputRef = node;
	};

	_onClickAway = e => {
		if (this._node.contains && !this._node.contains(e.target)) {
			const { focused } = this.props;
			const isFocused = focused === null ? this.state.initialFoucsed : focused;

			if (isFocused) {
				this._focusController(false);
			}
		} else {
			this._focusController(true);
		}
	};
	_onKeyDown = e => {
		if (e.keyCode === 27 || e.keyCode === 13) {
			this._focusController(false);
		}
	};
	_focusController = toBeFocused => {
		const { onFocusChange, focused, onChange } = this.props;
		if (focused == null) {
			this.setState({
				initialFoucsed: toBeFocused,
			});
		}
		onChange && onChange(this._getFormattedTime());
		onFocusChange && onFocusChange(toBeFocused);
	};
	render() {
		const {
			dir,
			focused,
			time,
			showSec,
			disabled,
			className,
			style,
			timeInputExtraOptions,
			getLocaleString,
			...others
		} = this.props;

		const { h, m, s, a } = this.state.time;

		const inputProps = {
			value: this.state.value,
			iconLeft: time32.id,
			onFocus: () => {
				this._focusController(true);
			},
			onChange: this._onChange,
			onKeyDown: this._onKeyDown,
			disabled: disabled || !time,
			ref: this._inputRef,
			'data-tid': 'ba-common-time-picker__input',
			dir,
		};
		const isFocused = !disabled && focused === null ? this.state.initialFoucsed : focused;

		if (showSec) {
			inputProps.step = '1';
		}
		const classNames = cx(timepickerClassName, { focused: isFocused, showSec });

		return (
			<div
				{...sanitize(others, TimePicker)}
				role="region"
				aria-label={getLocaleString('timePicker')}
				className={`${classNames} ${className}`}
				ref={this._ref}
				style={style}
			>
				<BaseInput
					{...timeInputExtraOptions}
					{...inputProps}
					ref={this._inputRef}
					dir={dir}
				/>
				{isFocused && (
					<div
						className={`${timepickerClassName}__time-slots`}
						onKeyDown={this._onKeyDown}
						dir="ltr"
					>
						<TimeSlot
							getLocaleString={getLocaleString}
							onChange={num => {
								this.setState({ time: this._getNewTimeByUnit(num, 'h') });
							}}
							unit="h"
							index={parseInt(h - 1, 10)}
						/>
						<TimeSlot
							getLocaleString={getLocaleString}
							onChange={num => {
								this.setState({ time: this._getNewTimeByUnit(num, 'm') });
							}}
							unit="m"
							index={parseInt(m, 10)}
						/>
						{showSec && (
							<TimeSlot
								getLocaleString={getLocaleString}
								onChange={num => {
									this.setState({ time: this._getNewTimeByUnit(num, 's') });
								}}
								unit="s"
								index={parseInt(s, 10)}
							/>
						)}
						<TimeSlot
							getLocaleString={getLocaleString}
							onChange={num => {
								this.setState({ time: this._getNewTimeByUnit(num, 'a') });
							}}
							unit="a"
							index={parseInt(a, 10)}
						/>
					</div>
				)}
			</div>
		);
	}
}
